import { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import { ArrowLeft } from "lucide-react";
import OtpForm from "./OtpForm";
import GradientButton from "../shared/GradientButton";
import { login, getUserDetails, initiatePayment } from "../shared/api";

export default function HomePage() {
  const [step, setStep] = useState("login");
  const [mobile, setMobile] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [users, setUsers] = useState(null);
  const [error, setError] = useState("");

  const mobileInputRef = useRef(null);

  useEffect(() => {
    if (step === "login" && mobileInputRef.current) {
      mobileInputRef.current.focus();
    }
  }, [step]);

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setMobile(value);
  };

  const handleLogin = async (e) => {
    try {
      if (e) e.preventDefault();
      if (!/^\d{10}$/.test(mobile)) {
        setError("Mobile number must be 10 digits");
        return;
      }
      setLoading(true);
      const { data } = await login({ mobile_no: mobile });
      setSessionId(data?.session_id); setLoading(false); setStep("otp"); setError("");
    } catch (err) {
      setLoading(false);
      setError(err.response?.data?.message);
    }
  };

  const handleOtpVerify = async (code) => {
    try {
      const { data } = await getUserDetails({ session_id: sessionId, code });
      setMobile(""); setUsers(data); setLoading(false); setStep("users");
    } catch (err) {
      setStep("login"); setLoading(false);
      setError(err.response?.data?.message);
    }
  };

  const initiate = async (user) => {
    try {
      const { data } = await initiatePayment({ session_id: sessionId, user_id: user.user_id });
      window.location.href = data?.url;
    } catch (err) {
      setError(err.response?.data?.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center text-accent px-4 w-full">
      <div className="relative z-10 bg-darkbg/80 backdrop-blur-md border border-accentLight rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="flex flex-col items-center text-center">
          <img src="/logo.png" alt="MavX Logo" className="w-32 mb-2" />
          <h5 className="text-md font-bold mb-4 text-center text-[#D0413A]">
            Quick Pay
          </h5>
        </div>

        {step === "login" && (
          <form onSubmit={handleLogin} className="flex flex-col items-center w-full p-4">
            <label className="block text-sm mb-2 text-left w-full">Mobile Number</label>
            <div className="relative w-full mb-2">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-accentLight">
                +91
              </span>
              <input
                type="text"
                ref={mobileInputRef}
                placeholder="Your mobile number"
                value={mobile}
                onChange={handleMobileChange}
                maxLength={10}
                className="w-full h-12 pl-14 pr-3 border border-accentLight rounded-lg bg-transparent text-accent placeholder-accentLight focus:outline-none"
                required
              />
            </div>
            {error && <p className="text-left w-full text-red-400 text-sm mt-2 mb-2">{error}</p>}
            <GradientButton type="submit" loading={loading}
              className="w-full py-3 rounded-lg font-semibold hover:bg-white transition mt-5">
              Send OTP
            </GradientButton>
          </form>
        )}

        {step === "otp" && (
          <OtpForm onVerify={handleOtpVerify} onResend={handleLogin} />
        )}

        {step === "users" && (
          <div>
            <h3 className="text-xl font-semibold mb-4 text-center text-[#D0413A]">Available Connections</h3>
            <div className="space-y-3">
              {users.map((user) => {
                return (
                  <button
                    key={user.user_id}
                    onClick={() => initiate(user)}
                    className={
                      `relative w-full text-left p-4 rounded-lg font-semibold transition border-2
                      ${user.overdue ?
                        "border-red-500 bg-red-500/10 hover:bg-red-500/20" :
                        "border-accentLight bg-darkbg hover:bg-accent hover:text-black"
                      }`
                    }>
                    <h4 className="absolute top-4 right-3 text-lg font-bold">
                      ₹{user.amount}
                    </h4>
                    <h3 className="text-xl font-semibold">{user.name}</h3>
                    {user.invoice_no ?
                      <p className="mt-2 text-sm font-light opacity-70">Invoice #: {user.invoice_no.join(', ')}</p> :
                      <p className="mt-2 text-sm font-light opacity-70">Plan: {user.plan_name}</p>
                    }
                    <p className="text-sm font-light opacity-70">
                      Due Date: {format(user.due_date, "dd MMM yyyy")}
                    </p>
                  </button>
                );
              })}
              <GradientButton
                onClick={() => setStep("login")}
                className="w-full py-3 rounded-lg font-semibold hover:bg-white transition mt-5">
                <ArrowLeft size={22} /> Go Back
              </GradientButton>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}